/**
 * Roadmap (Ideas) Page
 *
 * Ports the read/display side of Flutter's RoadmapScreen
 * (lib/presentation/screens/roadmap/roadmap_screen.dart:474-677): an "Ideas"
 * app bar, the disclaimer header ("Have a great idea…" + the "Idea Rewards
 * terms" link + the "Add idea" button), and a paginated list of
 * RoadmapListItem cards with infinite scroll and a trailing loader while
 * fetching the next page (Flutter's `_hasMore && _isFetching`).
 *
 * Architecture: Page → useRoadmap Hook → UseCase → Service
 *
 * Page responsibilities (per presentation.md page/hook split):
 * - Owns `useBasePage` (lifecycle `onInit → init`) and the `screenKey` mint.
 * - Reads the optional notification deep-link payload from `location.state` —
 *   a `RoadmapItemInfoEntity` plus the `RoadmapCommentInfoEntity` Flutter
 *   always sends alongside it (push_navigation_service.dart:243-300). The item
 *   info goes to the hook, which fetches the single referenced item instead of
 *   the paginated list (Flutter's `widget.itemInfo != null` branch,
 *   roadmap_screen.dart:417-425); the comment info is screen-scoped
 *   (`RoadmapScreen.commentInfo`, roadmap_screen.dart:35) and is handed to the
 *   comments sheet by BOTH openers (see `handleComments`).
 * - Performs the deep-link auto-open: the hook surfaces the intent
 *   (`pendingDeepLinkOpen`); the page opens the actual sheet once — likes →
 *   `RoadmapItemLikesSheet.show(...)` with the hook-computed count, comments →
 *   `RoadmapCommentsSheet.show(...)` for the deep-linked item. Mirrors Flutter's
 *   post-fetch `openComments`/`openLikes` branch (roadmap_screen.dart:457-471);
 *   the open is page-owned because it needs the page-minted `screenKey`.
 * - Provides the action handlers. Like / dislike port Flutter's `_likeTapped`
 *   / `_dislikeTapped` (roadmap_screen.dart:189-351): the owner short-circuit
 *   (`currentUser.firebaseId == item.userId`) opens `RoadmapItemLikesSheet`
 *   with the matching count + `likedType` and records no reaction; the
 *   non-owner path delegates to the hook's `toggleReaction`. Tapping the comment
 *   action opens the roadmap comments sheet (`RoadmapCommentsSheet.show`,
 *   Flutter's `_commentsTapped`, roadmap_screen.dart:353-362). Tapping the author
 *   navigates to user-details (`Navigator.navigateToUserDetails`). Show-support
 *   remains deferred — it routes to `InfoSheet.showNotImplementedYet`. The card
 *   body itself has no tap handler, matching Flutter's `_itemTapped` no-op
 *   (roadmap_screen.dart:162-164, whose only statement is commented out).
 * - Mirrors a card's comment count when the sheet emits
 *   `ROADMAP_ITEM_COMMENTS_UPDATED` (add/reply): subscribes for the page's
 *   lifetime and calls the hook's `patchItemComments` — the web equivalent of
 *   Flutter's shared-entity reference where the sheet bumps
 *   `roadmapItemEntity.comments` directly (roadmap_screen.dart:663-671).
 * - Wires the post-add reload: the "Add idea" button (in
 *   `RoadmapDisclaimerHeader`) navigates to the Add Roadmap page, which emits
 *   `ROADMAP_ITEM_ADDED` on a successful submit. This page subscribes to that
 *   event, re-fetches the user's latest item via the hook's
 *   `prependLatestUserItem`, inserts it at the top, and scrolls the list to the
 *   top — Flutter's `_onAddRoadmapItemTapped` reload (roadmap_screen.dart:145-153).
 *
 * Routing / Navigator method / screenKey prefix / HomeTopBarBody entry are
 * wired separately in Task 10.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '@/presentation/store';
import {
  FlexibleHeaderList,
  type FlexibleHeaderListHandle,
} from '@/presentation/components/common/appBar/flexibleHeaderList/FlexibleHeaderList';
import { ContentGridItemState } from '@/presentation/components/common/content/ContentGridItemState';
import { InfoSheet } from '@/presentation/components/common/sheet/info/InfoSheet';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useBasePage } from '@/presentation/hooks';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import { Navigator } from '@/presentation/routing/Navigator';
import { eventBus, APP_EVENT_KEYS } from '@/infrastructure/eventBus';
import type { RoadmapItemEntity } from '@/domain/entities/roadmap/roadmapItemEntity';
import type { RoadmapItemInfoEntity } from '@/domain/entities/info/roadmapItemInfoEntity';
import type { RoadmapCommentInfoEntity } from '@/domain/entities/info/roadmapCommentInfoEntity';
import { RoadmapDeepLinkOpenKind } from '@/domain/enums/roadmap/roadmapDeepLinkOpenKind';
import { useRoadmap } from './hooks/useRoadmap';
import { RoadmapDisclaimerHeader } from './components/RoadmapDisclaimerHeader';
import { RoadmapListItem } from './components/RoadmapListItem';
import { RoadmapCommentsSheet } from './components/comments/RoadmapCommentsSheet';
import { RoadmapItemLikesSheet } from './components/sheets/roadmapItemLikes/RoadmapItemLikesSheet';

/** Shape of the optional deep-link payload passed via `location.state`. */
interface RoadmapLocationState {
  roadmapItemInfo?: RoadmapItemInfoEntity;
  roadmapCommentInfo?: RoadmapCommentInfoEntity;
}

export const RoadmapPage = () => {
  const location = useLocation();
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();

  // The current user — used for the owner short-circuit (Flutter's
  // `currentUser.firebaseId == item.userId`) and to supply the like request's
  // username / userId. Pattern: useContentDetails.ts:42.
  const currentUser = useSelector((state: RootState) => state.auth.user);

  // Notification deep-link payload: when the page is opened from a roadmap
  // notification, `location.state.roadmapItemInfo` carries the item id so the
  // hook fetches that single item instead of the paginated list. Snapshotted
  // once (page-level concern per the page/hook split) so it survives re-renders.
  const roadmapItemInfoRef = useRef<RoadmapItemInfoEntity | undefined>(
    (location.state as RoadmapLocationState | null)?.roadmapItemInfo,
  );
  const roadmapItemInfo = roadmapItemInfoRef.current;

  // The second half of the same deep-link payload: the comment the notification
  // referenced (Flutter passes both, push_navigation_service.dart:243-300).
  // Screen-scoped like Flutter's `RoadmapScreen.commentInfo`
  // (roadmap_screen.dart:35), so it is snapshotted once on mount too — a later
  // `location.state` change must not re-arm the highlight.
  const roadmapCommentInfoRef = useRef<RoadmapCommentInfoEntity | undefined>(
    (location.state as RoadmapLocationState | null)?.roadmapCommentInfo,
  );
  const roadmapCommentInfo = roadmapCommentInfoRef.current;

  const { currentScreenKeyRef } = useKeepAlive();
  const screenKeyRef = useRef<string>(
    currentScreenKeyRef.current || generateScreenKey(location.pathname),
  );
  const screenKey = screenKeyRef.current;

  // Imperative handle on the list so the post-add reload can animate the scroll
  // back to the top — the web equivalent of Flutter's
  // `_scrollController.animateTo(0, 300ms, ease)` (roadmap_screen.dart:149-153).
  const listRef = useRef<FlexibleHeaderListHandle>(null);

  const {
    items,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    pendingDeepLinkOpen,
    consumePendingDeepLinkOpen,
    init,
    loadMore,
    toggleReaction,
    prependLatestUserItem,
    patchItemComments,
  } = useRoadmap(roadmapItemInfo);

  useBasePage({
    screenKey,
    onInit: () => {
      void init();
    },
    cleanReactQueryBehaviour: 'onPop',
  });

  // Post-add reload: the add page emits `ROADMAP_ITEM_ADDED` on a successful
  // submit (no payload). On receipt, re-fetch the user's latest item, prepend
  // it, and scroll to the top — Flutter's `_onAddRoadmapItemTapped` reload
  // (roadmap_screen.dart:145-153). Registered for the page's lifetime and torn
  // down on unmount via the `eventBus.on` disposer (no leak / no double-prepend
  // when the page is revisited). Skipped in single-item notification mode.
  useEffect(() => {
    if (roadmapItemInfo != null) {
      return;
    }
    const unsubscribe = eventBus.on(APP_EVENT_KEYS.ROADMAP_ITEM_ADDED, () => {
      void (async () => {
        await prependLatestUserItem(currentUser?.firebaseId ?? '');
        listRef.current?.scrollToIndex(0, 'smooth');
      })();
    });
    return unsubscribe;
  }, [roadmapItemInfo, prependLatestUserItem, currentUser]);

  // Comment-count mirror: the comments sheet emits `ROADMAP_ITEM_COMMENTS_UPDATED`
  // ({ roadmapItemId, comments }) when a comment/reply is added; mirror the new
  // total onto the matching in-memory card — the web equivalent of Flutter's
  // shared-entity reference (roadmap_screen.dart:663-671). Registered for the
  // page's lifetime and torn down on unmount via the `eventBus.on` disposer.
  useEffect(() => {
    const unsubscribe = eventBus.on(
      APP_EVENT_KEYS.ROADMAP_ITEM_COMMENTS_UPDATED,
      ({ roadmapItemId, comments }) => {
        patchItemComments(roadmapItemId, comments);
      },
    );
    return unsubscribe;
  }, [patchItemComments]);

  const handleBack = useCallback(() => {
    Navigator.goBack();
  }, []);

  // Tapping the author avatar/name navigates to the user-details page —
  // Flutter's `_userTapped` (roadmap_screen.dart:366-368).
  const handleUserTap = useCallback((userId: string) => {
    Navigator.navigateToUserDetails(userId);
  }, []);

  // Tapping the comment action opens the roadmap comments sheet for the tapped
  // item — Flutter's `_commentsTapped` (roadmap_screen.dart:353-362). The
  // deep-link `commentInfo` is screen-scoped in Flutter, not per-open: the sole
  // `RoadmapCommentsBottomSheet` render site always reads the widget param
  // (`commentInfo: widget.commentInfo`, roadmap_screen.dart:668), and BOTH
  // openers funnel through the same `_commentsTapped` — the deep-link auto-open
  // (:460) and the item action row (`commentsTapped: _commentsTapped`, :646).
  // So the action-row opener passes the same mount-stable ref. On a normally
  // entered board it is `undefined` and nothing is highlighted; in single-item
  // deep-link mode the list holds only the deep-linked item, so the highlight
  // can never land on a different item. `screenKey` scopes the sheet to this
  // page (mirrors the Add flow's containment).
  const handleComments = useCallback(
    (item: RoadmapItemEntity) => {
      RoadmapCommentsSheet.show({
        item,
        commentInfo: roadmapCommentInfo,
        screenKey,
        fixedHeight: responsive.bottomSheet.maxHeight,
      });
    },
    [screenKey, responsive, roadmapCommentInfo],
  );

  // Show-support stays deferred — route it to the canonical not-implemented sheet.
  const handleNotImplemented = useCallback(() => {
    InfoSheet.showNotImplementedYet({ l10n, screenKey });
  }, [l10n, screenKey]);

  // Like tap — Flutter's `_likeTapped` (roadmap_screen.dart:189-271). Owner
  // (`firebaseId == item.userId`) opens the likes sheet with `item.likes` +
  // `likedType=true` and records no reaction; non-owner toggles via the hook.
  const handleLike = useCallback(
    (item: RoadmapItemEntity) => {
      // Flutter always has a resolved current user on this auth-gated screen;
      // fail safe here so a null user neither opens the sheet incorrectly nor
      // fires an empty-identity toggle.
      if (currentUser == null) {
        return;
      }
      if (currentUser.firebaseId === item.userId) {
        RoadmapItemLikesSheet.show({
          roadmapItemId: item.id,
          likedType: true,
          likes: item.likes,
          screenKey,
          fixedHeight: responsive.bottomSheet.maxHeight,
        });
        return;
      }
      void toggleReaction(item.id, true, currentUser.name, currentUser.firebaseId);
    },
    [currentUser, screenKey, toggleReaction, responsive],
  );

  // Dislike tap — Flutter's `_dislikeTapped` (roadmap_screen.dart:273-351), the
  // mirror of `handleLike`: owner opens the sheet with `item.dislikes` +
  // `likedType=false`; non-owner toggles with `liked=false`.
  const handleDislike = useCallback(
    (item: RoadmapItemEntity) => {
      // See handleLike: fail safe when there is no authenticated user.
      if (currentUser == null) {
        return;
      }
      if (currentUser.firebaseId === item.userId) {
        RoadmapItemLikesSheet.show({
          roadmapItemId: item.id,
          likedType: false,
          likes: item.dislikes,
          screenKey,
          fixedHeight: responsive.bottomSheet.maxHeight,
        });
        return;
      }
      void toggleReaction(item.id, false, currentUser.name, currentUser.firebaseId);
    },
    [currentUser, screenKey, toggleReaction, responsive],
  );

  // Deep-link auto-open — Flutter's post-fetch branch (roadmap_screen.dart:457-471).
  // The hook surfaces the intent (`pendingDeepLinkOpen`); the page opens the
  // actual sheet because it owns the `screenKey`. Fires once, then clears the
  // intent so it can't re-open on a later re-render.
  useEffect(() => {
    if (pendingDeepLinkOpen == null) {
      return;
    }
    if (pendingDeepLinkOpen.kind === RoadmapDeepLinkOpenKind.likes) {
      RoadmapItemLikesSheet.show({
        roadmapItemId: pendingDeepLinkOpen.roadmapItemId,
        likedType: pendingDeepLinkOpen.likedType,
        likes: pendingDeepLinkOpen.count,
        screenKey,
        fixedHeight: responsive.bottomSheet.maxHeight,
      });
    } else {
      // `comments` — open the roadmap comments sheet for the deep-linked item,
      // mirroring Flutter's `openComments` → `_commentsTapped(_items[0])`
      // (roadmap_screen.dart:458-459). In single-item deep-link mode the fetched
      // item is the only entry in `items`; guard so a missing item opens nothing.
      const item = items.find((candidate) => candidate.id === pendingDeepLinkOpen.roadmapItemId);
      if (item != null) {
        RoadmapCommentsSheet.show({
          item,
          commentInfo: roadmapCommentInfo,
          screenKey,
          fixedHeight: responsive.bottomSheet.maxHeight,
        });
      }
    }
    consumePendingDeepLinkOpen();
  }, [
    pendingDeepLinkOpen,
    consumePendingDeepLinkOpen,
    screenKey,
    items,
    responsive,
    roadmapCommentInfo,
  ]);

  const renderRoadmapItem = useCallback(
    (item: RoadmapItemEntity) => (
      <RoadmapListItem
        item={item}
        onUserTap={handleUserTap}
        onLike={() => handleLike(item)}
        onDislike={() => handleDislike(item)}
        onComments={handleComments}
        onShowSupport={handleNotImplemented}
      />
    ),
    [handleUserTap, handleLike, handleDislike, handleComments, handleNotImplemented],
  );

  return (
    <FlexibleHeaderList
      ref={listRef}
      qa={{ id: 'roadmap', status: isLoading ? 'loading' : 'ready' }}
      listQa={{ id: 'roadmap-list' }}
      items={items}
      renderItem={renderRoadmapItem}
      getItemKey={(item) => item.id}
      onLoadMore={loadMore}
      hasMore={hasMore}
      isLoadingMore={isLoadingMore}
      emptyListComponent={
        <ContentGridItemState
          isLoading={isLoading}
          errorText={error ? l10n.roadmap.errorLoading : undefined}
          emptyText={l10n.common.noData}
        />
      }
      backgroundColor={theme.background}
      appBarOptions={{
        title: l10n.topBar.ideas,
        onLeadingTap: handleBack,
        backgroundColor: theme.background,
        flexibleSpace: {
          parallax: true,
          background: <RoadmapDisclaimerHeader />,
        },
      }}
    />
  );
};
